/* ----------------------------------------------------------------------------
   RateMatrix — the SHARED ProShip pricing grid (React port of m0.js openRateMatrix).
   Zone tabs at top · one row per weight slab · the full 19 ProShip pricing columns.
   Edited cells highlight amber. Reused by the onboarding Commercial step (A1) and
   the Update Rates & TAT "Edit in UI" step (C2), so the two paths cannot drift.

   <RateMatrix rows onChange /> — inline editable grid.
   <RateMatrixDialog open rows title subtitle onConfirm onClose /> — modal wrapper.
   ---------------------------------------------------------------------------- */
import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import CheckIcon from '@mui/icons-material/Check'
import {PRICING_COLS, ZONE_LABEL} from '../dummyData/pricing'
import Modal from './Modal'
import {cx} from './helpers'

const EDIT_STYLE = {backgroundColor: '#FFF6E0', borderColor: '#D69E2E'}

export default function RateMatrix({rows = [], onChange}) {
  // working copy + a frozen baseline (mount-time) to detect edited cells
  const baseline = useRef(rows.map((r) => ({...r})))
  const [data, setData] = useState(() => rows.map((r) => ({...r})))

  const zones = useMemo(() => {
    const z = [1, 2, 3, 4, 5].filter((zn) => data.some((r) => +r.zone === zn))
    return z.length ? z : [1]
  }, [data])
  const [active, setActive] = useState(zones[0])

  const editedCount = useMemo(() => {
    let n = 0
    data.forEach((r, i) => {
      const b = baseline.current[i] || {}
      PRICING_COLS.forEach((c) => {
        if (c.kind === 'zone' || c.kind === 'json') return
        if (String(r[c.key]) !== String(b[c.key])) n++
      })
    })
    return n
  }, [data])

  const setCell = (ri, key, val) => {
    setData((prev) => {
      const next = prev.map((r) => ({...r}))
      next[ri][key] = val
      if (onChange) onChange(next, countEdits(next, baseline.current))
      return next
    })
  }

  const visible = data.map((r, ri) => ({r, ri})).filter((x) => +x.r.zone === active)

  return (
    <div>
      <div className='rm-tabs'>
        {zones.map((z) => (
          <button
            type='button'
            key={z}
            className={cx('rm-tab', z === active && 'on')}
            onClick={() => setActive(z)}
          >
            {'Zone ' + z + (ZONE_LABEL[z] ? ' · ' + ZONE_LABEL[z] : '')}
          </button>
        ))}
      </div>
      <div className='rm-scroll'>
        <table className='rm-tbl'>
          <thead>
            <tr>
              {PRICING_COLS.map((c) => (
                <th className='rm-th' key={c.key}>
                  {c.hd}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(({r, ri}) => {
              const b = baseline.current[ri] || {}
              return (
                <tr key={ri}>
                  {PRICING_COLS.map((c) => {
                    if (c.kind === 'zone') return <td className='rm-zc' key={c.key}>{r.zone}</td>
                    if (c.kind === 'json')
                      return (
                        <td className='rm-json' key={c.key} title={r[c.key] || ''}>
                          <span className='mono'>
                            {String(r[c.key] || '').length > 16
                              ? String(r[c.key]).slice(0, 16) + '…'
                              : String(r[c.key] || '')}
                          </span>
                        </td>
                      )
                    const edited = String(r[c.key]) !== String(b[c.key])
                    if (c.kind === 'op' || c.kind === 'type') {
                      const opts = c.kind === 'op' ? ['MAX', 'MIN'] : ['VARIABLE', 'FIXED']
                      return (
                        <td key={c.key}>
                          <select
                            className='input rmcell'
                            style={edited ? EDIT_STYLE : undefined}
                            value={r[c.key]}
                            onChange={(e) => setCell(ri, c.key, e.target.value)}
                          >
                            {opts.map((o) => (
                              <option key={o}>{o}</option>
                            ))}
                          </select>
                        </td>
                      )
                    }
                    return (
                      <td key={c.key}>
                        <input
                          className={cx('input', 'rmcell', 'rm-num', c.primary && 'rm-primary')}
                          style={edited ? EDIT_STYLE : undefined}
                          value={r[c.key]}
                          onChange={(e) => setCell(ri, c.key, e.target.value)}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className='row center gap8' style={{marginTop: 10, fontSize: 12, color: 'var(--neutral)'}}>
        <InfoOutlinedIcon style={{width: 13, height: 13, flexShrink: 0}} />
        <span>
          Zone tabs at top · one row per weight slab · matches the ProShip pricing upload. Edited cells
          highlight amber. <b>{editedCount}</b> edited.
        </span>
      </div>
    </div>
  )
}

function countEdits(data, baseline) {
  let n = 0
  data.forEach((r, i) => {
    const b = baseline[i] || {}
    PRICING_COLS.forEach((c) => {
      if (c.kind === 'zone' || c.kind === 'json') return
      if (String(r[c.key]) !== String(b[c.key])) n++
    })
  })
  return n
}

RateMatrix.propTypes = {
  rows: PropTypes.array,
  onChange: PropTypes.func,
}

export function RateMatrixDialog({
  open,
  onClose,
  title = 'Rate Card — Preview & Edit',
  subtitle,
  rows = [],
  confirmLabel = 'Confirm & Use',
  onConfirm,
}) {
  const work = useRef({rows: rows.map((r) => ({...r})), edited: 0})
  // reset working copy whenever the dialog (re)opens with new rows
  const key = open ? rows.length + ':' + (rows[0] ? rows[0].price : '') : 'closed'
  const lastKey = useRef(null)
  if (open && lastKey.current !== key) {
    work.current = {rows: rows.map((r) => ({...r})), edited: 0}
    lastKey.current = key
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      icon={<TableChartOutlinedIcon fontSize='small' />}
      width='min(1180px, 97vw)'
      actions={[
        {label: 'Cancel', onClick: onClose},
        {
          label: confirmLabel,
          primary: true,
          icon: <CheckIcon fontSize='small' />,
          onClick: () => {
            if (onConfirm) onConfirm({rows: work.current.rows, edited: work.current.edited})
          },
        },
      ]}
    >
      {subtitle ? (
        <div className='muted' style={{fontSize: 12.5, marginBottom: 10}}>
          {subtitle}
        </div>
      ) : null}
      <RateMatrix
        rows={rows}
        onChange={(next, edited) => {
          work.current = {rows: next, edited}
        }}
      />
    </Modal>
  )
}

RateMatrixDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  rows: PropTypes.array,
  confirmLabel: PropTypes.string,
  onConfirm: PropTypes.func,
}
